import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, MapPin, ArrowUpRight, ArrowRight, CalendarPlus, X, Maximize2, Apple, Calendar, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { CommunityNav } from "@/components/community/CommunityNav";
import { api } from "@/lib/api";
import { hasSchedule, googleCalendarUrl, outlookCalendarUrl, downloadIcs } from "@/lib/calendar";

const SITE = "https://lagooncityrealestate.ca";

function AddToCalendar({ ev }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  if (!hasSchedule(ev)) return null;
  return (
    <div className="relative mt-5" ref={ref}>
      <button
        data-testid={`event-addcal-${ev.event_id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex items-center gap-2 rounded-full border border-[#E890A7]/35 bg-[#E890A7]/10 px-4 py-2 text-xs font-medium text-[#F2A7BA] hover:bg-[#E890A7]/20 transition-colors"
      >
        <CalendarPlus size={14} /> Add to calendar
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}
            className="absolute left-0 bottom-full z-30 mb-2 w-48 overflow-hidden rounded-xl border border-[#E890A7]/20 bg-[#1A161F] shadow-xl shadow-black/40"
          >
            <a
              data-testid={`event-gcal-${ev.event_id}`}
              href={googleCalendarUrl(ev)} target="_blank" rel="noreferrer"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-200 hover:bg-[#E890A7]/10 transition-colors"
            >
              <Calendar size={15} className="text-[#E890A7]" /> Google Calendar
            </a>
            <a
              data-testid={`event-outlook-${ev.event_id}`}
              href={outlookCalendarUrl(ev)} target="_blank" rel="noreferrer"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-200 hover:bg-[#E890A7]/10 transition-colors border-t border-[#E890A7]/10"
            >
              <Mail size={15} className="text-[#E890A7]" /> Outlook Calendar
            </a>
            <button
              data-testid={`event-ical-${ev.event_id}`}
              onClick={(e) => { e.stopPropagation(); downloadIcs(ev); setOpen(false); }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-200 hover:bg-[#E890A7]/10 transition-colors border-t border-[#E890A7]/10"
            >
              <Apple size={15} className="text-[#E890A7]" /> Apple / iCal
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CommunityEvents() {
  const [events, setEvents] = useState([]);
  const [flyer, setFlyer] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setFlyer(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>Community Events — Lagoon City | Joselle Alves</title>
        <meta name="description" content="What's happening in Lagoon City — local events, gatherings and things to do around the canals and Lake Simcoe." />
        <link rel="canonical" href={`${SITE}/community/events`} />
      </Helmet>
      <PageHeader />
      <CommunityNav />

      <section className="relative overflow-hidden">
        <div className="absolute -right-40 top-0 w-[30rem] h-[30rem] rounded-full bg-[#E890A7]/8 blur-[120px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-10 sm:pt-20 sm:pb-12">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#F2A7BA] mb-4 flex items-center gap-2"><CalendarDays size={14} /> Community Events</p>
          <h1 data-testid="events-title" className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
            What&apos;s happening in <em className="italic text-[#E890A7]">Lagoon City</em>
          </h1>
          <p className="mt-6 max-w-2xl text-slate-300 font-light text-lg">
            From dock parties and marina events to seasonal gatherings around Lake Simcoe — here's what's coming up in
            our waterfront community.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        {events.length === 0 ? (
          <div data-testid="events-empty" className="rounded-[2rem] border border-dashed border-[#E890A7]/25 bg-[#151218]/50 p-10 sm:p-16 text-center">
            <CalendarDays size={40} className="text-[#E890A7]/70 mx-auto mb-6" />
            <h2 className="font-display font-light text-2xl sm:text-3xl">Events coming soon</h2>
            <p className="mt-3 max-w-xl mx-auto text-slate-400 font-light">
              We're putting together a calendar of local Lagoon City happenings. Check back shortly — or reach out to
              Joselle if you're hosting something you'd like featured here.
            </p>
            <Link to="/#contact" data-testid="events-contact-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 hover:bg-[#F2A7BA] transition-colors">
              Tell Joselle about an event <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {events.map((ev) => (
              <motion.div key={ev.event_id} data-testid={`event-card-${ev.event_id}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
                className="group rounded-3xl overflow-hidden border border-[#E890A7]/15 bg-[#151218]/70 hover:border-[#E890A7]/45 hover:-translate-y-1 transition-all duration-300 flex flex-col">
                {ev.img && (
                  <button
                    type="button"
                    data-testid={`event-flyer-open-${ev.event_id}`}
                    onClick={() => setFlyer(ev)}
                    className="relative h-44 overflow-hidden block w-full"
                    aria-label={`View full flyer for ${ev.title}`}
                  >
                    <img src={ev.img} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
                    <span className="absolute inset-0 bg-[#0D0B0E]/0 group-hover:bg-[#0D0B0E]/30 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 rounded-full bg-[#0D0B0E]/70 backdrop-blur px-3 py-1.5 text-xs text-white">
                        <Maximize2 size={13} /> View flyer
                      </span>
                    </span>
                  </button>
                )}
                <div className="p-6 flex flex-col flex-1">
                  {ev.date && <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E890A7]/80">{ev.date}</p>}
                  <p className="font-display text-2xl mt-2 leading-snug">{ev.title}</p>
                  {ev.location && <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400"><MapPin size={12} className="text-[#E890A7]" /> {ev.location}</p>}
                  <p className="text-sm text-slate-400 mt-2 font-light leading-relaxed flex-1">{ev.description}</p>
                  <AddToCalendar ev={ev} />
                  {ev.url && (
                    <a href={ev.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-[#F2A7BA] hover:gap-2 transition-all duration-300">Details <ArrowUpRight size={12} /></a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Full flyer lightbox */}
      <AnimatePresence>
        {flyer && (
          <motion.div
            data-testid="event-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0D0B0E]/90 backdrop-blur-sm p-4 sm:p-8"
            onClick={() => setFlyer(null)}
          >
            <button
              data-testid="event-lightbox-close"
              onClick={() => setFlyer(null)}
              className="absolute top-5 right-5 w-11 h-11 rounded-full border border-white/20 bg-[#0D0B0E]/60 flex items-center justify-center text-white hover:bg-[#E890A7]/20 transition-colors"
              aria-label="Close flyer"
            >
              <X size={20} />
            </button>
            <motion.img
              key={flyer.event_id}
              src={flyer.img}
              alt={flyer.title}
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] max-w-full w-auto rounded-xl shadow-2xl shadow-black/60 object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
